import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useLayoutEffect, useRef } from "react";
import { useSizeClass } from "./useSizeClass.js";
import { Icon } from "../icons/Icon.js";
import { KopfRahmenKontext } from "../kopfleiste/MeKopfleiste.js";
import { Spalten, Spalte } from "../me/index.js";
import "./MeSchale.css";
/** Wunschbreiten der Spalten in px; das Element rechnet sie in rem um. */
const LISTE_BREITE = 320;
const KONTEXT_BREITE = 280;
/**
 * Mindestbreite der Akte (Fokusspalte), ab der `me-spalten` die Liste einfaltet. Nicht
 * geraten: Am Telefon zeigt die Schale denselben Akteninhalt einspaltig und randlos — geprüft
 * u. a. an der schmalsten Vorgabe in e2e/faltung.spec.ts (iPhone 15, 393px). 360 liegt mit
 * Abstand darunter. Ohne sie gälte die ME-Vorgabe 520 — dann fiele die Liste auf dem iPad
 * quer (1024) mit offenem Kontext in den Stapel (250 + 520 + 280 > 1024), wo sie neben der
 * Akte steht.
 */
const AKTE_MINDESTBREITE = 360;
/**
 * Schwelle, unter der die Kontextspalte die Akte überlagert statt sie zu verdrängen
 * (Auftraggeber-Entscheidung vom 30.08., .superpowers/sdd/2026-08-30-paket1-fundament/
 * ueberlagern-report.md). Gemessen wird sie von `me-spalten` selbst, an der Breite seines
 * Arbeitsbereichs.
 *
 * Rechnung (aus der abgebauten AppShell, Stand 10.09.2026 ohne Navigationsspalte): Liste
 * auf dem iPad 264 + Kontext 280 + Griff 18 verlangen zusammen 562px; damit der Akte dabei
 * mindestens AKTE_MINDESTBREITE bleibt, muss der Bereich 562 + 360 = 922px breit sein —
 * sonst wird überlagert statt gedrängt. Die Zahl bleibt bewusst stehen, auch wenn die
 * Wunschbreiten auf me-spalten (Liste 320, Griff 24) etwas anders rechnen: Sie ist die
 * erprobte und in e2e/faltung.spec.ts festgehaltene Grenze.
 */
export const KONTEXT_SCHWELLE = 264 + KONTEXT_BREITE + 18 + AKTE_MINDESTBREITE; // = 922
/**
 * Zeichnet die Schale gerade den Telefon-Einspalter (Liste ODER Objekt)? Für die
 * Anwendungslogik, die nur dort gilt — etwa die Markierung der zuletzt geöffneten Zeile nach
 * dem Rückweg (App.tsx). Dieselbe Grenze wie im Rahmen selbst: Grössenklasse „phone" aus
 * useSizeClass, also unter 744px (Entscheid 44, Dirk 08.10.2026, über me-design Entscheid 23;
 * me-design fragt seit 2d27041 dieselbe Grenze ab: `TELEFON_ABFRAGE` = max-width 743px).
 */
export function useEinspalter() {
    return useSizeClass() === "phone";
}
/**
 * Der gefaltete Rahmen auf `me-spalten` (ME-Etappe 2, Aufgabe 2).
 *
 * Telefon (unter 744px, Grössenklasse „phone"): wie bisher genau EINE Spalte — Liste oder Akte per `showList` —,
 * Tableiste unten, Wortmarke und Rückweg im Kopf. `me-spalten` steht dort bewusst NICHT:
 * welche Spalte am Telefon zu sehen ist, entscheidet die Route, und das ist
 * Anwendungslogik, keine Faltung (Entscheid zur Etappe).
 *
 * iPad/Desktop: `me-spalten` mit Liste (nur wenn das Ziel eine hat), Akte als Fokusspalte
 * und — nur wenn das Ziel einen hat — der Kontextspalte rechts. Klappgriff, Schleier,
 * Überlagern unter der Schwelle und Escape im überlagernden Zustand kommen aus dem Element.
 * Eine offene Kundeninfo-Ebene (Menü, Dialog) geht bei Escape vor: Der Escape-Stapel
 * horcht in der Capture-Phase und meldet ein behandeltes Escape mit `preventDefault()`,
 * das das Element beachtet (useEscapeEbene.ts).
 *
 * Der Klappzustand der Kontextspalte gehört weiter dem BENUTZER (`panels`, gemerkt in
 * App.tsx). Die Schale überträgt ihn in das Element (`kontextFalten`/`kontextAusfalten`)
 * und meldet nur ZURÜCK, was der Benutzer von Hand getan hat (`vonHand`). Was das Element
 * von sich aus tut — es lässt den Handzustand bei jedem Breitenwechsel verfallen und kehrt
 * zu seiner Automatik zurück —, überschreibt den gemerkten Zustand NICHT; die Schale setzt
 * ihn danach wieder ein.
 *
 * Auf dem Desktop steht die Kontextspalte wie bisher dauerhaft offen und hat keinen
 * Falt-Knopf (`faltbar={false}`); `panels.context` gilt dort nicht.
 */
export function MeSchale(p) {
    const klasse = useSizeClass();
    const desktop = klasse === "desktop";
    const hatListe = p.list !== undefined && p.list !== null;
    const hatKontext = p.context !== undefined && p.context !== null;
    /** Soll die Kontextspalte offen stehen? Desktop immer, sonst nach Wunsch des Benutzers. */
    const offen = desktop || p.panels.context;
    const offenRef = useRef(offen);
    offenRef.current = offen;
    const onPanelsChangeRef = useRef(p.onPanelsChange);
    onPanelsChangeRef.current = p.onPanelsChange;
    const spaltenRef = useRef(null);
    /** Die Listenspalte — am Telefon verborgen (`hidden`), solange das Objekt gezeigt wird.
     *  Die Hülle `Spalte` reicht kein `hidden` durch; gesetzt wird es am Host. */
    const listeRef = useRef(null);
    const listeVerborgen = klasse === "phone" && !p.showList;
    useLayoutEffect(() => {
        listeRef.current?.toggleAttribute("hidden", listeVerborgen);
    });
    /** Überträgt den gewünschten Stand in das Element (als Handzustand). */
    const einsetzen = useCallback(() => {
        const el = spaltenRef.current;
        if (el === null)
            return;
        if (offenRef.current)
            el.kontextAusfalten();
        else
            el.kontextFalten();
    }, []);
    // Beim Einhängen und bei jeder Änderung des Wunsches. Layout-Effekt, damit der erste
    // gemalte Stand schon der gemerkte ist (kein Aufklappen-und-wieder-Zuklappen).
    useLayoutEffect(() => {
        if (klasse !== "phone" && hatKontext)
            einsetzen();
    }, [offen, klasse, hatKontext, einsetzen]);
    const beiKontextGefaltet = useCallback((d) => {
        const istOffen = !d.gefaltet;
        if (istOffen === offenRef.current)
            return;
        if (!d.vonHand) {
            // Automatik nach einem Breitenwechsel: nicht melden, den gemerkten Stand wieder
            // einsetzen. Aufgeschoben, weil das Element gerade mitten im Einpassen ist und einen
            // Aufruf darin verwürfe.
            queueMicrotask(einsetzen);
            return;
        }
        // Den Wunsch SOFORT nachziehen, nicht erst mit dem nächsten Render: Bis React den neuen
        // Zustand zeichnet, hielte `offenRef` sonst den alten Wunsch — und eine Automatik-Meldung
        // in dieser Lücke (etwa ein Breitenwechsel direkt nach dem Klick auf Griff oder Schleier)
        // setzte genau den Stand wieder ein, den der Benutzer eben verlassen hat (Flackern in
        // e2e/faltung.spec.ts, 850px Schleier/Escape, ME-Etappe 2 Aufgabe 6).
        offenRef.current = istOffen;
        onPanelsChangeRef.current({ context: istOffen });
    }, [einsetzen]);
    /*
     * Der Horcher für `me-kontext-gefaltet` hängt im LAYOUT-Effekt am Element und nicht über
     * das Prop `onKontextGefaltet` der Hülle: Die Hülle meldet sich in einem passiven Effekt
     * an, und der läuft nach einer Aktualisierung ausserhalb von `act` (Sitzung geladen,
     * Rahmen zum ersten Mal gezeichnet) erst NACH dem Malen. Ein Klick auf den Griff in
     * dieser Lücke klappte die Spalte auf, ohne dass der Benutzerzustand davon erfuhr
     * (belegt in web/test/randbereiche.test.tsx: unter Last rot). Der Layout-Effekt läuft
     * im selben Takt wie das Einhängen — die Lücke gibt es nicht.
     */
    const zweispaltig = klasse !== "phone";
    useLayoutEffect(() => {
        const el = spaltenRef.current;
        if (!zweispaltig || el === null)
            return undefined;
        const zuhoerer = (ereignis) => {
            beiKontextGefaltet(ereignis.detail);
        };
        el.addEventListener("me-kontext-gefaltet", zuhoerer);
        return () => el.removeEventListener("me-kontext-gefaltet", zuhoerer);
    }, [zweispaltig, beiKontextGefaltet]);
    const telefon = klasse === "phone";
    /*
     * EIN Baum für alle Grössenklassen (ME-Etappe 4, P8). Bis dahin zeichnete das Telefon
     * einen eigenen Zweig (`<main>` statt `me-spalten`). Beim Wechsel der Klasse — ein Tablet
     * oder Telefon wird gedreht, ein Fenster schmaler gezogen — tauschte React deshalb den
     * ganzen Teilbaum aus: Die Akte hing ab und neu auf, und mit ihr jede offene Maske samt
     * Eingaben (Befund P5: „Auftrag bearbeiten“ schloss sich bei 1280 → 390 px).
     *
     * Jetzt steht die Akte in jeder Klasse an DERSELBEN Stelle (zweites Kind von
     * `me-spalten`, Spalte „Akte“) und überlebt den Wechsel. Am Telefon zeigt `me-spalten`
     * von sich aus nur die letzte Spalte in voller Breite, faltet nichts und zeichnet keine
     * Schiene (Telefonabfrage im Element, dieselbe Grenze 744). WELCHE Spalte dort die letzte
     * ist, bleibt Anwendungslogik (Entscheid der Etappe 2): Zeigt die Route die Liste, steht
     * keine Akte im Baum; zeigt sie das Objekt, ist die Liste verborgen (`hidden`), aber
     * eingehängt — ihr Scrollstand überdauert den Rückweg (Hausregel „Scroll bei
     * Neuzeichnen“, Review-Fokus 4 der Etappe 2). Die Kontextspalte gibt es am Telefon nicht.
     *
     * Kopf, Rückweg und Tableiste bleiben, wie sie waren: Der Kopf klebt am Telefon, die
     * Tableiste steht unter dem Arbeitsbereich. Beide stehen um `me-spalten` herum und
     * verschieben dessen Platz im Baum nicht.
     */
    const akteZeigen = !(telefon && p.showList && hatListe);
    return (_jsxs("div", { className: `ki-shell ki-shell--${klasse}`, children: [_jsx(KopfRahmenKontext.Provider, { value: telefon ? {
                    vorn: (_jsxs(_Fragment, { children: [p.wortmarke, !p.showList && p.zurueck !== undefined && (_jsx("button", { type: "button", className: "ki-shell__back", "aria-label": p.zurueck.beschriftung, onClick: p.zurueck.aktion, children: _jsx(Icon, { name: "chevron-left", size: 18 }) }))] })),
                    klassen: "ki-shell__header ki-shell__header--sticky",
                } : { vorn: p.wortmarke, klassen: "ki-shell__header" }, children: p.header }), _jsxs(Spalten, { ref: spaltenRef, ueberlauf: "stapel", fokus: "rest", fokusMin: AKTE_MINDESTBREITE, kontextSchwelle: KONTEXT_SCHWELLE, ...(telefon ? { className: "ki-shell__main" } : {}), children: [hatListe && (_jsx(Spalte, { ref: listeRef, titel: "Liste", breite: LISTE_BREITE, schmal: klasse === "tablet", children: p.list })), akteZeigen && _jsx(Spalte, { titel: "Akte", children: p.detail }), hatKontext && !telefon && (_jsx(Spalte, { titel: "Kontext", kontext: true, breite: KONTEXT_BREITE, faltbar: !desktop, children: p.context }))] }), telefon && _jsx("nav", { className: "ki-shell__tabbar", children: p.tabBar })] }));
}
//# sourceMappingURL=MeSchale.js.map